import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { NotificationFeedbackType } from 'expo-haptics';
import {
  LoyaltyChallengeCardsRail,
  theme,
  triggerHapticNotificationFeedback,
} from '@sg/garnish';

import type { ChallengesQuery } from '@order/graphql';
import { useChallengeOptInMutation } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useChallengesTelemetry } from '../../../ChallengesScreen/ChallengesScreen.telemetry';
import type { SweetpassHomeChallengesRailContent } from '../../hooks';
import {
  isChallengeStarted,
  serializeChallenge,
} from './SweetpassHomeChallengesRail.utils';

export const SweetpassHomeChallengesRail = ({
  content,
  challenges,
  availableCount,
  isFetching,
}: SweetpassHomeChallengesRailProps) => {
  const { t } = useLocalizationContext();
  const { handleChallengesStartTelemetry } = useChallengesTelemetry();
  const [, applyChallengeOptInMutation] = useChallengeOptInMutation();
  const { handleChallengesTermsViewTelemetry } = useChallengesTelemetry();

  const [loadingChallengeId, setLoadingChallengeId] = useState<string | null>(
    null,
  );

  const onStartChallenge = useCallback(
    async (id: string) => {
      setLoadingChallengeId(id);
      handleChallengesStartTelemetry(id);

      // ─────────────────────────────────────────────────────────────────

      const { data: responseData } = await applyChallengeOptInMutation({
        challengeId: id,
      });

      setLoadingChallengeId(null);

      const isSuccessfullyStartedChallenge = isChallengeStarted(
        responseData?.challengeOptIn.status,
      );

      // ─── Haptic feedback ─────────────────────────────────────────────

      if (!isSuccessfullyStartedChallenge) {
        await triggerHapticNotificationFeedback(
          NotificationFeedbackType.Warning,
        );

        return;
      }

      await triggerHapticNotificationFeedback(NotificationFeedbackType.Success);
    },
    [handleChallengesStartTelemetry, applyChallengeOptInMutation],
  );

  return (
    <View style={styles.container}>
      <LoyaltyChallengeCardsRail
        headerText={content.titleText}
        headerVariation="display"
        subtitle={content.bodyText}
        emptyStateDescription={content.emptyContentText}
        emptyStateImage={{ uri: content.emptyContentImage.url }}
        isLoading={isFetching}
        termsUrl="https://www.sweetgreen.com/terms"
        availableCount={availableCount}
        onStartChallenge={onStartChallenge}
        newBadgeText={t('sweetpass.home.challenges-rail.new-badge')}
        deadlinePrefixText={t('sweetpass.home.challenges-rail.deadline-prefix')}
        completionText={t('sweetpass.home.challenges-rail.completion-text')}
        terms={t('general.terms-and-conditions')}
        applyText={t('general.apply').toLowerCase()}
        challenges={
          challenges?.map(serializeChallenge(loadingChallengeId)) ?? []
        }
        onPressTermsAndConditions={handleChallengesTermsViewTelemetry}
      />
    </View>
  );
};

// ––– STYLES –––––––––––––––––––––––––––––

const styles = StyleSheet.create({
  container: {
    marginBottom: theme.spacing.large,
  },
});

// ––– TYPES –––––––––––––––––––––––––––––

type SweetpassHomeChallengesRailProps = {
  readonly content: SweetpassHomeChallengesRailContent;
  readonly challenges: ChallengesQuery['challenges'];
  readonly availableCount: number;
  readonly isFetching: boolean;
};
